import { REF_PATTERN } from "../../shared/types.ts";
import { ApiError, sendRef } from "./api.ts";

const KEY = "headcount.ref";
/** A ref older than this is dropped, so a link clicked weeks ago doesn't claim a signup it didn't bring. */
const MAX_AGE = 30 * 864e5;

/**
 * Remembers `?ref=` (or `?utm_source=`) from the link the visitor arrived on. The first one wins until it is sent or goes
 * stale, so a later visit through another link doesn't take the credit.
 */
export function captureRef(): void {
  const params = new URLSearchParams(location.search);
  const ref = (params.get("ref") ?? params.get("utm_source") ?? "").trim().toLowerCase();
  if (!REF_PATTERN.test(ref)) return;
  try {
    if (stored()) return;
    localStorage.setItem(KEY, JSON.stringify({ ref, at: Date.now() }));
  } catch {}
}

function stored(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as { ref?: unknown; at?: unknown } | null;
    if (!v || typeof v.ref !== "string" || typeof v.at !== "number" || Date.now() - v.at > MAX_AGE) {
      localStorage.removeItem(KEY);
      return null;
    }
    return REF_PATTERN.test(v.ref) ? v.ref : null;
  } catch {
    return null;
  }
}

/**
 * Sends the remembered ref once the visitor is signed in. The database only takes it for an account made in the last day,
 * so sending it from an older account is harmless. It is cleared once the server answers, kept to retry when the call fails.
 */
export function flushRef(): void {
  const ref = stored();
  if (!ref) return;
  const clear = () => {
    try {
      localStorage.removeItem(KEY);
    } catch {}
  };
  sendRef(ref).then(clear, (e) => {
    if (e instanceof ApiError && e.status >= 400 && e.status < 500) clear();
  });
}
